import { useCallback, useEffect, useMemo, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Box, Grid, media, Stack, xcss } from "@atlaskit/primitives";

import { ufoEventRedirectToProduct } from "../../../../../../../../components/signup/utils";
import { JiraOnboardingContainer } from "../../components/jira-onboarding-container/jira-onboarding-container";
import { useCloudStatus, useCloudStatusRedirection } from "../../hooks";
import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useCreateRedirectUrlWithWizard } from "../onboarding-wizard/utils/create-redirect-url";

import { SetupProps, StepContext } from "./types";

const BATCH_ANALYTICS_INTERVAL = 100;

export const Setup = <T extends StepContext>({ screens }: SetupProps<T>) => {
    const { cloudId: tenantId } = useCloudStatus();

    const {
        userSegmentationContext: { selectedTeamType: teamType }
    } = useConfirmationPage();

    const { resolveBlockingRedirectPromises, createRedirectUrl } =
        useCloudStatusRedirection();

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

    const [screenIndex, setScreenIndex] = useState(0);
    const [context, setContext] = useState<T>(
        () =>
            ({
                tenantId,
                template: "KANBAN",
                teamType
            } as T)
    );

    const currentStep = useMemo(
        () => screens[screenIndex],
        [screenIndex, screens]
    );

    useEffect(() => {
        setContext((prevContext) => ({
            ...prevContext,
            tenantId
        }));
    }, [tenantId]);

    const onComplete = useCallback(
        async (taskId?: string, onRedirect?: () => void) => {
            const productUrl = createRedirectUrl();

            if (productUrl) {
                await resolveBlockingRedirectPromises();

                if (onRedirect) {
                    try {
                        onRedirect();
                    } catch (error) {
                        // NO-OP, prevent redirect from failing
                    }
                }

                createAnalyticsEvent({
                    eventName: "redirectedToProduct",
                    eventComponent: "confirmationPage",
                    event: "redirected",
                    eventType: "ui",
                    action: "redirected",
                    attributes: {
                        reactComponent: "JiraProvisioningWithOnboardingWizard"
                    }
                }).fire();

                // SLO "good event" for redirect into product
                ufoEventRedirectToProduct.success({
                    customData: {
                        analyticsEvent: "redirected",
                        redirectFrom: "confirmationPage",
                        reactComponent: "JiraProvisioningWithOnboardingWizard",
                        finalProvisioningScreen: "jira-onboarding-wizard"
                    }
                });

                setTimeout(
                    () =>
                        window.location.assign(
                            createRedirectUrlWithWizard(productUrl, taskId)
                        ),
                    BATCH_ANALYTICS_INTERVAL
                );
            }
        },
        [
            createAnalyticsEvent,
            createRedirectUrl,
            createRedirectUrlWithWizard,
            resolveBlockingRedirectPromises
        ]
    );

    const nextStep = useCallback(() => {
        if (screenIndex < screens.length - 1) {
            setScreenIndex(screenIndex + 1);
        } else {
            onComplete(context?.taskId);
        }
    }, [context, screenIndex, screens.length, onComplete]);

    return (
        <JiraOnboardingContainer header={""} subheader={""}>
            {currentStep && currentStep.type === "full" && (
                <currentStep.ScreenComponent
                    context={context}
                    setContext={setContext}
                    nextStep={nextStep}
                    screenId={currentStep.screenId}
                />
            )}
            {currentStep && currentStep.type === "split" && (
                <Grid xcss={gridStyles} columnGap="space.0" rowGap="space.0">
                    <Box xcss={wrapperStyles}>
                        <Stack spread="space-between" xcss={stackStyles}>
                            <currentStep.LeftComponent
                                context={context}
                                setContext={setContext}
                                nextStep={nextStep}
                                screenId={currentStep.screenId}
                            />
                        </Stack>
                    </Box>
                    <Box xcss={wrapperStyles}>
                        <currentStep.RightComponent
                            context={context}
                            setContext={setContext}
                            nextStep={nextStep}
                            screenId={currentStep.screenId}
                        />
                    </Box>
                </Grid>
            )}
        </JiraOnboardingContainer>
    );
};

const gridStyles = xcss({
    width: "100%",
    height: "100%",
    gridTemplateColumns: "100%",
    [media.above.sm]: {
        gridTemplateColumns: "50% 50%"
    }
});

const wrapperStyles = xcss({
    backgroundColor: "elevation.surface",
    gap: "space.025"
});

const stackStyles = xcss({
    height: "100%"
});
